import React, { useState, useEffect } from 'react';
import { NotificationManager } from 'react-notifications';
import useLoading from '../../../hooks/useLoading';
import { Link } from 'react-router-dom'; // 🟢 Importamos Link para los enlaces a los folios
import { 
    apiGetTarjetasRevisionGerente, 
    apiAprobarGrupoTarjetas, 
    apiRechazarTarjetas 
} from '../../../lib/apiPaseLista';

const RevisionGerente = () => {
    const loading = useLoading();
    const [grupos, setGrupos] = useState([]);
    
    // Estados para el flujo de Rechazo
    const [grupoExpandido, setGrupoExpandido] = useState(null);
    const [tarjetasSeleccionadas, setTarjetasSeleccionadas] = useState([]);

    const cargarTarjetas = async () => {
        loading.set();
        try {
            const resp = await apiGetTarjetasRevisionGerente();
            if (!resp.error) {
                agruparTarjetas(resp.data || []);
            }
        } catch (error) {
            NotificationManager.error('Error al cargar las tarjetas en revisión.', '', 5000);
        }
        loading.stop();
    };

    useEffect(() => {
        cargarTarjetas();
        // eslint-disable-next-line
    }, []);

    const agruparTarjetas = (tarjetasPlanas) => {
        const agrupadas = {};

        tarjetasPlanas.forEach(t => {
            const fechaStr = t.fecha.split('T')[0];
            const key = `${fechaStr}_${t.id_cultivo}_${t.id_actividad}`;

            if (!agrupadas[key]) {
                agrupadas[key] = {
                    key,
                    fecha: fechaStr,
                    // 🟢 Agregamos el ID (sys21_id) junto al nombre
                    cultivo: `${t.id_cultivo} - ${t.cultivo_nombre}`,
                    actividad: `${t.id_actividad} - ${t.actividad_nombre}`,
                    totalJornales: 0,
                    totalHorasExtra: 0,
                    tarjetas: []
                };
            }

            agrupadas[key].totalJornales += parseFloat(t.jornales || 0);
            agrupadas[key].totalHorasExtra += parseFloat(t.horas_extra || 0);
            agrupadas[key].tarjetas.push(t);
        });

        setGrupos(Object.values(agrupadas));
    };

    const handleAceptarGrupo = async (grupo) => {
        if (!window.confirm(`¿Aprobar el bloque de ${grupo.actividad} (${grupo.fecha})?`)) return;

        loading.set();
        try {
            const idsTarjetas = grupo.tarjetas.map(t => t.id_tarjeta);
            const resp = await apiAprobarGrupoTarjetas({ ids: idsTarjetas });
            
            if (!resp.error) {
                NotificationManager.success('Tarjetas aprobadas exitosamente.', '', 3000);
                setGrupoExpandido(null);
                cargarTarjetas();
            } else {
                NotificationManager.error(resp.message || 'Error al aprobar.', '', 5000);
            }
        } catch (error) {
            NotificationManager.error('Error interno del servidor.', '', 5000);
        }
        loading.stop();
    };

    const handleToggleRechazo = (key) => {
        if (grupoExpandido === key) {
            setGrupoExpandido(null);
            setTarjetasSeleccionadas([]);
        } else {
            setGrupoExpandido(key);
            setTarjetasSeleccionadas([]); 
        }
    };

    const handleCheckboxChange = (idTarjeta) => {
        setTarjetasSeleccionadas(prev => 
            prev.includes(idTarjeta) 
                ? prev.filter(id => id !== idTarjeta) 
                : [...prev, idTarjeta]
        );
    };

    const handleConfirmarRechazo = async () => {
        if (tarjetasSeleccionadas.length === 0) {
            return NotificationManager.warning('Seleccione al menos una tarjeta para rechazar.', '', 3000);
        }

        // 🟢 Confirmación actualizada
        if (!window.confirm(`¿Rechazar ${tarjetasSeleccionadas.length} tarjetas y APROBAR el resto del grupo?`)) return;

        loading.set();
        try {
            // 🟢 Buscamos las tarjetas del grupo expandido y separamos las buenas de las malas
            const grupoActual = grupos.find(g => g.key === grupoExpandido);
            const idsAprobadas = grupoActual.tarjetas
                .map(t => t.id_tarjeta)
                .filter(id => !tarjetasSeleccionadas.includes(id));

            // 🟢 Mandamos ambas listas al backend
            const resp = await apiRechazarTarjetas({ 
                idsRechazadas: tarjetasSeleccionadas,
                idsAprobadas: idsAprobadas
            });
            
            if (!resp.error) {
                NotificationManager.success('Grupo procesado correctamente.', '', 3000);
                setGrupoExpandido(null);
                setTarjetasSeleccionadas([]);
                cargarTarjetas(); // Recargamos la tabla
            } else {
                NotificationManager.error(resp.message || 'Error al rechazar.', '', 5000);
            }
        } catch (error) {
            NotificationManager.error('Error interno del servidor.', '', 5000);
        }
        loading.stop();
    };

    return (
        <div className="card shadow-sm mb-4">
            {/* 🟢 Título removido, empieza directo en el body de la tarjeta */}
            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table table-hover mb-0">
                        <thead className="thead-light text-center">
                            <tr>
                                <th>Fecha</th>
                                <th>Cultivo</th>
                                <th>Actividad</th>
                                <th>Total Jornales</th>
                                <th>Total H.E.</th>
                                <th>Tarjetas</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {grupos.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted py-4">
                                        No hay tarjetas pendientes de revisión gerencial.
                                    </td>
                                </tr>
                            ) : (
                                grupos.map(grupo => (
                                    <React.Fragment key={grupo.key}>
                                        <tr className="text-center align-middle">
                                            <td className="font-weight-bold">{grupo.fecha}</td>
                                            <td>{grupo.cultivo}</td>
                                            <td>{grupo.actividad}</td>
                                            <td><span className="badge badge-warning text-dark px-3 py-2" style={{ fontSize: '14px'}}>{grupo.totalJornales}</span></td>
                                            <td><span className="badge badge-warning text-dark px-3 py-2" style={{ fontSize: '14px'}}>{grupo.totalHorasExtra}</span></td>
                                            <td>{grupo.tarjetas.length}</td>
                                            <td>
                                                {/* 🟢 Botón Aceptar: Se deshabilita si este grupo está expandido */}
                                                <button 
                                                    className="btn btn-sm btn-success mr-2"
                                                    onClick={() => handleAceptarGrupo(grupo)}
                                                    title="Aprobar todo el grupo"
                                                    disabled={grupoExpandido === grupo.key} 
                                                >
                                                    <i className="fas fa-check" /> Aceptar
                                                </button>
                                                
                                                {/* 🟢 Botón Rechazar: Se deshabilita si este grupo ya está expandido */}
                                                <button 
                                                    className={`btn btn-sm ${grupoExpandido === grupo.key ? 'btn-secondary' : 'btn-danger'}`}
                                                    onClick={() => handleToggleRechazo(grupo.key)}
                                                    title="Ver tarjetas para rechazar"
                                                    disabled={grupoExpandido === grupo.key} 
                                                >
                                                    <i className="fas fa-times" /> Rechazar...
                                                </button>
                                            </td>
                                        </tr>

                                        {grupoExpandido === grupo.key && (
                                            <tr className="bg-light">
                                                <td colSpan="7" className="p-4 border-bottom">
                                                    {/* 🟢 Texto de modo rechazo más descriptivo */}
                                                    <div className="alert alert-secondary mb-3">
                                                        <i className="fas fa-info-circle mr-2"/>
                                                        <strong>Instrucciones:</strong> Seleccione las tarjetas específicas que no autoriza y deben ser corregidas (Puedes dar click al folio para ver detalles). Estas serán devueltas a la bandeja del Secretario para su revisión o justificación.
                                                    </div>
                                                    
                                                    <table className="table table-sm table-bordered bg-white text-center" style={{ width: 'auto', margin: '0 auto' }}>
                                                        <thead className="thead-dark">
                                                            <tr>
                                                                <th style={{ width: '50px' }}>Selección</th>
                                                                <th>Folio Tarjeta</th>
                                                                <th>Lotes</th> {/* 🟢 Nueva columna */}
                                                                <th>Jornales</th>
                                                                <th>Horas Extra</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {grupo.tarjetas.map(t => (
                                                                <tr key={t.id_tarjeta}>
                                                                    <td>
                                                                        <div className="custom-control custom-checkbox">
                                                                            <input 
                                                                                type="checkbox" 
                                                                                className="custom-control-input" 
                                                                                id={`chk_${t.id_tarjeta}`}
                                                                                checked={tarjetasSeleccionadas.includes(t.id_tarjeta)}
                                                                                onChange={() => handleCheckboxChange(t.id_tarjeta)}
                                                                            />
                                                                            <label className="custom-control-label" htmlFor={`chk_${t.id_tarjeta}`}></label>
                                                                        </div>
                                                                    </td>
                                                                    <td className="font-weight-bold">
                                                                        <Link to={`/paselista/detalle/${t.id_tarjeta}`} target="_blank" rel="noopener noreferrer">
                                                                            #{t.folio || t.id_tarjeta}
                                                                        </Link>
                                                                    </td>
                                                                    {/* 🟢 Mostramos los lotes concatenados */}
                                                                    <td className="text-muted small">
                                                                        {t.lotes_nombres || 'Sin lote asignado'}
                                                                    </td>
                                                                    <td>{t.jornales}</td>
                                                                    <td>{t.horas_extra}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>

                                                    <div className="text-center mt-3">
                                                        <button 
                                                            className="btn btn-outline-secondary mr-2"
                                                            onClick={() => handleToggleRechazo(grupo.key)}
                                                        >
                                                            Cancelar
                                                        </button>
                                                        <button 
                                                            className="btn btn-danger"
                                                            onClick={handleConfirmarRechazo}
                                                            disabled={tarjetasSeleccionadas.length === 0}
                                                        >
                                                            <i className="fas fa-paper-plane mr-2"/>
                                                            Confirmar Envío a Secretario ({tarjetasSeleccionadas.length})
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default RevisionGerente;